import React, { useState, useMemo } from 'react';
import { ColumnDef, SortingState, PaginationState } from '@tanstack/react-table';
import { BugReport } from '../../types/bugReport';
import { TableComponent } from '../ui/TableComponent/TableComponent';
import { Chip, Box } from '@mui/material';
import { BugReportDialog } from './BugReportDialog';

interface BugReportListProps {
  bugReports: BugReport[];
  onEdit?: (bugReport: BugReport) => void;
  onDelete?: (bugReportId: string) => void;
}

export function BugReportList({ bugReports, onEdit, onDelete }: BugReportListProps) {
  // Default sorting by updatedAt (newest first)
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'updatedAt', desc: true }
  ]);
  const [selectedBugReport, setSelectedBugReport] = useState<BugReport | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Sort data with default sorting
  const sortedData = useMemo(() => {
    let sortedBugReports = [...bugReports];
    
    // Apply sorting
    if (sorting.length > 0) {
      const sort = sorting[0];
      sortedBugReports.sort((a, b) => {
        let aValue: any = a[sort.id as keyof BugReport];
        let bValue: any = b[sort.id as keyof BugReport];
        
        // Handle date sorting
        if (sort.id === 'createdAt' || sort.id === 'updatedAt') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // Handle string sorting
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return sort.desc ? 1 : -1;
        if (aValue > bValue) return sort.desc ? -1 : 1;
        return 0;
      });
    }
    
    return sortedBugReports;
  }, [bugReports, sorting]);

  const handleViewDetails = (bugReport: BugReport) => {
    setSelectedBugReport(bugReport);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedBugReport(null);
  };

  const columns: ColumnDef<BugReport>[] = [
    {
      accessorKey: 'title',
      header: 'Başlık',
      size: 150,
      cell: ({ getValue, row }) => (
        <Box sx={{ py: 1 }}>
          <div className="font-semibold text-gray-900 text-sm leading-5 mb-1">
            {getValue() as string}
          </div>
          
        </Box>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Durum',
      size: 80,
      cell: ({ getValue }) => {
        const status = getValue() as BugReport['status'];
        return (
          <Chip
            label={getStatusLabel(status)}
            sx={{
              ...getStatusStyle(status),
              fontWeight: 600,
              fontSize: '0.75rem',
              height: '28px',
              borderRadius: '14px',
              '& .MuiChip-label': {
                px: 2
              }
            }}
          />
        );
      },
    },
    {
      accessorKey: 'priority',
      header: 'Öncelik',
      size: 80,
      cell: ({ getValue }) => {
        const priority = getValue() as BugReport['priority'];
        return (
          <Chip
            label={getPriorityLabel(priority)}
            sx={{
              ...getPriorityStyle(priority),
              fontWeight: 600,
              fontSize: '0.75rem',
              height: '28px',
              borderRadius: '14px',
              '& .MuiChip-label': {
                px: 2
              }
            }}
          />
        );
      },
    },
    {
      accessorKey: 'reporter',
      header: 'Raporlayan',
      size: 120,
      cell: ({ getValue }) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-white rounded-full flex items-center justify-center text-blue-900 text-xs font-semibold mr-3">
            {(getValue() as string).charAt(0).toUpperCase()}
          </div>
          <div className="text-gray-700 text-sm font-medium">
            {getValue() as string}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Oluşturulma',
      size: 110,
      cell: ({ getValue }) => (
        <div className="text-gray-600 text-sm">
          {new Date(getValue() as string).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })}
        </div>
      ),
    },
    {
      accessorKey: 'updatedAt',
      header: 'Güncellenme',
      size: 110,
      cell: ({ getValue }) => (
        <div className="text-gray-600 text-sm">
          {new Date(getValue() as string).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })}
        </div>
      ),
    },
  ];

  return (
    <Box sx={{ 
      bgcolor: 'background.default',
      minHeight: '100vh',
      p: 3
    }}>
      <TableComponent
        data={sortedData}
        columns={columns}
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={handleViewDetails}
      />
      
      <BugReportDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        bugReport={selectedBugReport}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </Box>
  );
}

function getStatusLabel(status: BugReport['status']): string {
  switch (status) {
    case 'open':
      return 'Açık';
    case 'in-progress':
      return 'Devam Ediyor';
    case 'resolved':
      return 'Çözüldü';
    case 'closed':
      return 'Kapalı';
    default:
      return status;
  }
}

function getStatusStyle(status: BugReport['status']) {
  switch (status) {
    case 'open':
      return {
        backgroundColor: '#EBF8FF',
        color: '#1E40AF',
        border: '1px solid #BFDBFE'
      };
    case 'in-progress':
      return {
        backgroundColor: '#FEF3C7',
        color: '#D97706',
        border: '1px solid #FDE68A'
      };
    case 'resolved':
      return {
        backgroundColor: '#D1FAE5',
        color: '#059669',
        border: '1px solid #A7F3D0'
      };
    case 'closed':
      return {
        backgroundColor: '#F3F4F6',
        color: '#6B7280',
        border: '1px solid #D1D5DB'
      };
    default:
      return {
        backgroundColor: '#F3F4F6',
        color: '#6B7280',
        border: '1px solid #D1D5DB'
      };
  }
}

function getPriorityLabel(priority: BugReport['priority']): string {
  switch (priority) {
    case 'low':
      return 'Düşük';
    case 'medium':
      return 'Orta';
    case 'high':
      return 'Yüksek';
    case 'critical':
      return 'Kritik';
    default:
      return priority;
  }
}

function getPriorityStyle(priority: BugReport['priority']) {
  switch (priority) {
    case 'low':
      return {
        backgroundColor: '#F0F9FF',
        color: '#0369A1',
        border: '1px solid #BAE6FD'
      };
    case 'medium':
      return {
        backgroundColor: '#FFFBEB',
        color: '#D97706',
        border: '1px solid #FED7AA'
      };
    case 'high':
      return {
        backgroundColor: '#FEF2F2',
        color: '#DC2626',
        border: '1px solid #FECACA'
      };
    case 'critical':
      return {
        backgroundColor: '#FDF2F8',
        color: '#BE185D',
        border: '1px solid #FBCFE8'
      };
    default:
      return {
        backgroundColor: '#F3F4F6',
        color: '#6B7280',
        border: '1px solid #D1D5DB'
      };
  }
} 